/**
 * OWNED BY C5. `usePwa()` — everything the shell needs to know about installability,
 * connectivity, storage durability and pending updates (F-701, F-703, N-008).
 *
 * Every browser API touched here is feature-detected; on a browser with none of them
 * the hook degrades to `{ online: true, isStandalone: false, canInstall: false, … }`.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MAX_INSTALL_DISMISSALS, installCopyFor, type InstallCopy } from "./copy";
import { applyUpdate as applyServiceWorkerUpdate, getSwUpdateState, subscribeSwUpdate } from "./register";

export type InstallHint = "android" | "ios" | null;
export type InstallOutcome = "accepted" | "dismissed" | "unavailable";
export type Platform = "ios" | "android" | "other";

export interface PwaState {
  /** Navigator connectivity. Optimistic by design: `true` does not guarantee reachability. */
  online: boolean;
  /** Running as an installed app (any display-mode that is not a browser tab). */
  isStandalone: boolean;
  /** A `beforeinstallprompt` event is banked and `promptInstall()` will show the OS sheet. */
  canInstall: boolean;
  promptInstall: () => Promise<InstallOutcome>;
  /** Which install instructions to show, or null when there is nothing to say. */
  installHint: InstallHint;
  /** A new build is installed and waiting; the shell decides when to apply it. */
  updateReady: boolean;
  applyUpdate: () => Promise<boolean>;
  /** `navigator.storage.persisted()` — null while unknown/unsupported. */
  persisted: boolean | null;

  // ---- extras the shell may use; not part of the required contract ----
  /** Copy for `installHint`, including the iOS 7-day eviction explanation. */
  installCopy: InstallCopy | null;
  /** Record that the user dismissed the install prompt (hidden after 3 — F-703). */
  dismissInstall: () => void;
  installDismissals: number;
  platform: Platform;
  /** Re-request durable storage, e.g. from a Setup button. */
  requestPersistence: () => Promise<boolean>;
}

// ---------------------------------------------------------------------------
// beforeinstallprompt capture (module scope: the event usually fires before React mounts)
// ---------------------------------------------------------------------------

export interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
}

let deferredPrompt: BeforeInstallPromptEvent | null = null;
let installed = false;
const promptListeners = new Set<() => void>();
let wired = false;

function emitPromptChange(): void {
  for (const l of promptListeners) l();
}

function handleBeforeInstallPrompt(event: Event): void {
  event.preventDefault(); // keep Chrome's own mini-infobar out of the way
  deferredPrompt = event as BeforeInstallPromptEvent;
  emitPromptChange();
}

function handleAppInstalled(): void {
  deferredPrompt = null;
  installed = true;
  emitPromptChange();
}

/** Idempotent; called at import time and again from the hook so tests can re-wire. */
export function initInstallCapture(): void {
  if (wired || typeof window === "undefined" || typeof window.addEventListener !== "function") return;
  wired = true;
  window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
  window.addEventListener("appinstalled", handleAppInstalled);
}

/** Test seam. */
export function __resetInstallCapture(): void {
  if (wired && typeof window !== "undefined") {
    window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.removeEventListener("appinstalled", handleAppInstalled);
  }
  wired = false;
  deferredPrompt = null;
  installed = false;
  promptListeners.clear();
  persistRequested = false;
}

initInstallCapture();

// ---------------------------------------------------------------------------
// detection helpers (pure — unit-tested)
// ---------------------------------------------------------------------------

export function detectPlatform(userAgent: string, maxTouchPoints = 0): Platform {
  const ua = userAgent || "";
  if (/android/i.test(ua)) return "android";
  if (/iphone|ipad|ipod/i.test(ua)) return "ios";
  // iPadOS 13+ reports a desktop Safari UA; touch points give it away.
  if (/macintosh/i.test(ua) && maxTouchPoints > 1) return "ios";
  return "other";
}

/** WebKit-family browser on iOS — the ones subject to the 7-day eviction rule. */
export function isIosBrowser(userAgent: string, maxTouchPoints = 0): boolean {
  return detectPlatform(userAgent, maxTouchPoints) === "ios";
}

const STANDALONE_QUERIES = [
  "(display-mode: standalone)",
  "(display-mode: fullscreen)",
  "(display-mode: minimal-ui)",
  "(display-mode: window-controls-overlay)",
];

export function readStandalone(): boolean {
  if (typeof window === "undefined") return false;
  const nav = typeof navigator !== "undefined" ? (navigator as Navigator & { standalone?: boolean }) : undefined;
  if (nav?.standalone === true) return true; // iOS Safari home-screen app
  if (typeof window.matchMedia !== "function") return false;
  return STANDALONE_QUERIES.some((q) => {
    try {
      return window.matchMedia(q).matches === true;
    } catch {
      return false;
    }
  });
}

export function computeInstallHint(input: {
  platform: Platform;
  isStandalone: boolean;
  canInstall: boolean;
  dismissals: number;
}): InstallHint {
  if (input.isStandalone) return null;
  if (input.dismissals >= MAX_INSTALL_DISMISSALS) return null;
  if (input.canInstall) return input.platform === "ios" ? "ios" : "android";
  if (input.platform === "ios") return "ios"; // no beforeinstallprompt on iOS, ever
  return null;
}

// ---------------------------------------------------------------------------
// dismissal counter (UI preference only — register data lives in IndexedDB, F-702)
// ---------------------------------------------------------------------------

const DISMISS_KEY = "wc.install.dismissed";

export function readDismissals(): number {
  try {
    const raw = globalThis.localStorage?.getItem(DISMISS_KEY);
    const n = raw ? Number.parseInt(raw, 10) : 0;
    return Number.isFinite(n) && n > 0 ? n : 0;
  } catch {
    return 0;
  }
}

function writeDismissals(n: number): void {
  try {
    globalThis.localStorage?.setItem(DISMISS_KEY, String(n));
  } catch {
    /* private mode / storage disabled — the counter is a nicety, not a requirement */
  }
}

// ---------------------------------------------------------------------------
// persistent storage (F-703)
// ---------------------------------------------------------------------------

let persistRequested = false;

export async function ensurePersistentStorage(): Promise<boolean | null> {
  if (typeof navigator === "undefined" || !navigator.storage) return null;
  try {
    if (typeof navigator.storage.persisted === "function") {
      const already = await navigator.storage.persisted();
      if (already) return true;
    }
    if (typeof navigator.storage.persist !== "function") return false;
    return await navigator.storage.persist();
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// the hook
// ---------------------------------------------------------------------------

export interface UsePwaOptions {
  /** Ask for durable storage on mount (default true). C4 also asks when the DB opens. */
  autoPersist?: boolean;
}

export function usePwa(options: UsePwaOptions = {}): PwaState {
  const autoPersist = options.autoPersist ?? true;

  const [online, setOnline] = useState<boolean>(() =>
    typeof navigator === "undefined" || typeof navigator.onLine !== "boolean" ? true : navigator.onLine,
  );
  const [isStandalone, setIsStandalone] = useState<boolean>(() => readStandalone());
  const [canInstall, setCanInstall] = useState<boolean>(() => deferredPrompt !== null);
  const [updateReady, setUpdateReady] = useState<boolean>(() => getSwUpdateState().updateReady);
  const [persisted, setPersisted] = useState<boolean | null>(null);
  const [dismissals, setDismissals] = useState<number>(() => readDismissals());
  const mounted = useRef(true);

  const platform = useMemo<Platform>(
    () =>
      typeof navigator === "undefined"
        ? "other"
        : detectPlatform(navigator.userAgent ?? "", navigator.maxTouchPoints ?? 0),
    [],
  );

  // connectivity
  useEffect(() => {
    if (typeof window === "undefined") return;
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener("online", up);
    window.addEventListener("offline", down);
    return () => {
      window.removeEventListener("online", up);
      window.removeEventListener("offline", down);
    };
  }, []);

  // display-mode
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const sync = () => setIsStandalone(readStandalone());
    const queries = STANDALONE_QUERIES.map((q) => {
      try {
        return window.matchMedia(q);
      } catch {
        return null;
      }
    }).filter((q): q is MediaQueryList => q !== null);
    for (const q of queries) {
      if (typeof q.addEventListener === "function") q.addEventListener("change", sync);
      else if (typeof q.addListener === "function") q.addListener(sync);
    }
    sync();
    return () => {
      for (const q of queries) {
        if (typeof q.removeEventListener === "function") q.removeEventListener("change", sync);
        else if (typeof q.removeListener === "function") q.removeListener(sync);
      }
    };
  }, []);

  // install prompt availability
  useEffect(() => {
    initInstallCapture();
    const sync = () => {
      setCanInstall(deferredPrompt !== null);
      // After `appinstalled` the tab is still a tab until relaunch, so only re-read
      // the display-mode rather than asserting standalone.
      if (installed) setIsStandalone(readStandalone());
    };
    promptListeners.add(sync);
    sync();
    return () => {
      promptListeners.delete(sync);
    };
  }, []);

  // service-worker updates
  useEffect(() => {
    setUpdateReady(getSwUpdateState().updateReady);
    return subscribeSwUpdate((s) => setUpdateReady(s.updateReady));
  }, []);

  // durable storage
  useEffect(() => {
    mounted.current = true;
    let cancelled = false;
    void (async () => {
      if (typeof navigator === "undefined" || !navigator.storage) return;
      if (!autoPersist) {
        try {
          const already = (await navigator.storage.persisted?.()) ?? null;
          if (!cancelled) setPersisted(already);
        } catch {
          /* ignore */
        }
        return;
      }
      if (persistRequested) {
        try {
          const already = (await navigator.storage.persisted?.()) ?? null;
          if (!cancelled) setPersisted(already);
        } catch {
          /* ignore */
        }
        return;
      }
      persistRequested = true;
      const result = await ensurePersistentStorage();
      if (!cancelled) setPersisted(result);
    })();
    return () => {
      cancelled = true;
      mounted.current = false;
    };
  }, [autoPersist]);

  const promptInstall = useCallback(async (): Promise<InstallOutcome> => {
    const prompt = deferredPrompt;
    if (!prompt) return "unavailable";
    try {
      await prompt.prompt();
      const choice = await prompt.userChoice;
      if (choice.outcome === "accepted") {
        deferredPrompt = null;
        emitPromptChange();
        return "accepted";
      }
      // Chrome discards the event after a dismissal; do not offer a dead button.
      deferredPrompt = null;
      emitPromptChange();
      const next = readDismissals() + 1;
      writeDismissals(next);
      setDismissals(next);
      return "dismissed";
    } catch {
      return "unavailable";
    }
  }, []);

  const dismissInstall = useCallback(() => {
    const next = readDismissals() + 1;
    writeDismissals(next);
    setDismissals(next);
  }, []);

  const requestPersistence = useCallback(async () => {
    const result = await ensurePersistentStorage();
    setPersisted(result);
    return result === true;
  }, []);

  const installHint = useMemo(
    () => computeInstallHint({ platform, isStandalone, canInstall, dismissals }),
    [platform, isStandalone, canInstall, dismissals],
  );

  const installCopy = useMemo(() => installCopyFor(installHint), [installHint]);

  return {
    online,
    isStandalone,
    canInstall,
    promptInstall,
    installHint,
    updateReady,
    applyUpdate: applyServiceWorkerUpdate,
    persisted,
    installCopy,
    dismissInstall,
    installDismissals: dismissals,
    platform,
    requestPersistence,
  };
}
